import { useState, useEffect, useMemo } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";

const WhatsAppIcon = ({ color = "#25d366" }) => (
  <svg viewBox="0 0 24 24" fill={color} className="w-4 h-4">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const BUTTON_TEXTS = {
  ru: ["Выбрать букет", "Заказать цветы", "Оформить заказ", "Доставка сразу", "Купить свежим", "Собрать букет", "Успеть купить", "Цветы в заказ", "Взять сейчас", "Найти букет", "Нужны цветы", "Срочно купить"],
  en: ["Choose Bouquet", "Order Flowers", "Place Order", "Fast Delivery", "Buy Fresh", "Build Bouquet", "Hurry & Buy", "Flowers to Go", "Get Now", "Find Bouquet", "Need Flowers", "Buy Now"],
  tr: ["Buket Sec", "Cicek Siparis Et", "Siparis Ver", "Hemen Teslimat", "Taze Al", "Buket Olustur", "Acele Et", "Cicek Siparis", "Simdi Al", "Buket Bul", "Cicek Lazim", "Hemen Satin Al"],
};

// How many bouquets to show on mobile before the "Show more" button. Desktop always shows all.
const MOBILE_LIMIT = 12;
const SHOW_MORE_TEXT = { ru: "Посмотреть ещё", en: "Show more", tr: "Daha fazla" };

const ProductCard = ({ product, onOrderClick, onImageClick, cityName, currencySymbol = "$", convertPrice, t, lang = "ru", index = 0, isFirst = false, isBestseller = false, hiddenOnMobile = false }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const displayPrice = convertPrice ? convertPrice(product.price) : product.price;
  const displayName = lang === "tr" && product.name_tr ? product.name_tr : (lang === "en" && product.name_en ? product.name_en : product.name);
  const displayDesc = lang === "tr" && product.description_tr ? product.description_tr : (lang === "en" && product.description_en ? product.description_en : product.description);
  const btnText = BUTTON_TEXTS[lang]?.[index % BUTTON_TEXTS.ru.length] || BUTTON_TEXTS.ru[index % BUTTON_TEXTS.ru.length];
  const fakeOldPrice = isFirst ? Math.round(product.price * 1.25) : null;
  const isPremium = product.category === "premium";

  return (
    <div 
      className={`group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 ${isPremium ? "border-2 border-amber-400 shadow-amber-100" : isFirst ? "border-2 border-pink-400 shadow-pink-100" : isBestseller ? "border-2 border-emerald-400 shadow-emerald-100" : "border border-gray-100"} ${hiddenOnMobile ? "hidden sm:block" : ""}`}
      data-testid={`product-${product.id}`}
    >
      {/* Image */}
      <div 
        className="aspect-[4/5] relative overflow-hidden bg-gradient-to-br from-pink-50 to-rose-50 cursor-pointer"
        onClick={() => onImageClick(product)}
        data-testid={`product-image-${product.id}`}
      >
        {!imageLoaded && (
          <div className="absolute inset-0 bg-gradient-to-r from-pink-100 via-rose-100 to-pink-100 animate-pulse"></div>
        )}
        <img 
          src={product.image}
          alt={t ? t("alt.product", { name: displayName, city: cityName, price: `${currencySymbol}${displayPrice}` }) : `${displayName} — ${currencySymbol}${displayPrice}`}
          width={400}
          height={500}
          className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-110 ${
            imageLoaded ? "opacity-100" : "opacity-0"
          }`}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
        />
        {/* Price badge */}
        {!isPremium && (
          <div className="absolute top-4 right-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white px-4 py-2 rounded-full font-bold text-lg shadow-lg">
            {fakeOldPrice && (
              <span className="line-through text-pink-200 text-sm mr-1.5">{currencySymbol}{convertPrice ? convertPrice(fakeOldPrice) : fakeOldPrice}</span>
            )}
            {currencySymbol}{displayPrice}
          </div>
        )}
        {isFirst && !isPremium && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[9px] sm:text-xs font-bold px-2.5 py-0.5 sm:px-4 sm:py-1 rounded-full shadow-lg whitespace-nowrap">
            {lang === "tr" ? "EN UYGUN" : lang === "en" ? "BEST PRICE" : "ЛУЧШАЯ ЦЕНА"}
          </div>
        )}
        {isBestseller && !isPremium && !isFirst && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[9px] sm:text-xs font-bold px-2.5 py-0.5 sm:px-4 sm:py-1 rounded-full shadow-lg whitespace-nowrap" data-testid={`bestseller-badge-${product.id}`}>
            {lang === "tr" ? "ÇOK SATAN" : lang === "en" ? "BESTSELLER" : "ХИТ ПРОДАЖ"}
          </div>
        )}
        {isPremium && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-[9px] sm:text-xs font-bold px-3 py-0.5 sm:px-4 sm:py-1 rounded-full shadow-lg whitespace-nowrap">
            {lang === "tr" ? "Fiyat Sor" : lang === "en" ? "Ask Price" : "Узнать цену"}
          </div>
        )}
        {/* Preview hint on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg">
            <Search className="w-5 h-5 text-gray-700" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-5">
        <h3 className="font-bold text-gray-900 text-sm sm:text-lg mb-2">{displayName}</h3>
        {displayDesc && (
          <p className="text-gray-500 mb-3 sm:mb-4 text-xs sm:text-sm line-clamp-2">{displayDesc}</p>
        )}
        
        <Button 
          onClick={() => onOrderClick(product)}
          className={`w-full bg-white hover:bg-gray-50 text-gray-900 border-2 ${isPremium ? "border-amber-400 hover:border-amber-500" : "border-[#25d366]"} h-9 sm:h-12 rounded-full font-bold flex items-center justify-center gap-1.5 sm:gap-2 shadow-sm hover:shadow-md transition-all hover:scale-105 text-xs sm:text-sm px-2 sm:px-4`}
          data-testid={`order-btn-${product.id}`}
        >
          <WhatsAppIcon color={isPremium ? "#d97706" : "#25d366"} />
          <span>{btnText}</span>
        </Button>
      </div>
    </div>
  );
};

export const ProductsSection = ({ products, onOrderClick, onImageClick, cityName, currencySymbol = "$", convertPrice, lang = "ru", t }) => {
  const [mobileShowAll, setMobileShowAll] = useState(false);

  // One "Bestseller" bouquet per catalog: the mid-range bouquet closest to ~150 (in EUR/USD),
  // excluding the first (BEST PRICE) card and premium cards. Applies on both city & district pages.
  const bestsellerId = useMemo(() => {
    const candidates = products.filter((p, i) => i !== 0 && p.category !== "premium");
    if (!candidates.length) return null;
    let best = null;
    let bestDiff = Infinity;
    for (const p of candidates) {
      const diff = Math.abs((p.price || 0) - 150);
      if (diff < bestDiff) { best = p; bestDiff = diff; }
    }
    return best && bestDiff <= 80 ? best.id : null;
  }, [products]);

  // Reset mobile "show more" when the product list changes (e.g. category filter / city change)
  useEffect(() => {
    setMobileShowAll(false);
  }, [products.length]);

  return (
    <section id="catalog" className="py-12 sm:py-16 bg-gradient-to-b from-gray-50 to-white" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-14">
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {t ? t("products.title") : "Наши"}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500">{t ? t("products.titleAccent") : "букеты"}</span>
          </h2>
          <p className="text-gray-500 max-w-md mx-auto text-lg">
            {t ? t("products.subtitle") : "Закажите свежие цветы с доставкой в день заказа"}
          </p>
        </div>

        {/* Products Grid — desktop shows all, mobile shows MOBILE_LIMIT until "Show more" tapped */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {products.map((product, index) => (
            <ProductCard 
              key={product.id} 
              product={product} 
              onOrderClick={onOrderClick}
              onImageClick={onImageClick}
              cityName={cityName}
              currencySymbol={currencySymbol}
              convertPrice={convertPrice}
              t={t}
              lang={lang}
              index={index}
              isFirst={index === 0}
              isBestseller={product.id === bestsellerId}
              hiddenOnMobile={!mobileShowAll && index >= MOBILE_LIMIT}
            />
          ))}
        </div>

        {/* "Show more" button — mobile only (desktop shows all bouquets) */}
        {products.length > MOBILE_LIMIT && !mobileShowAll && (
          <div className="mt-8 text-center sm:hidden">
            <Button
              onClick={() => setMobileShowAll(true)}
              className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white rounded-full font-bold px-8 h-12 shadow-lg hover:shadow-xl transition-all"
              data-testid="show-more-mobile-btn"
            >
              {SHOW_MORE_TEXT[lang] || SHOW_MORE_TEXT.ru} ({products.length - MOBILE_LIMIT})
            </Button>
          </div>
        )}

        {products.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">В этой категории пока нет товаров</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductsSection;
